import axios from 'axios';

// Never try to refresh these — a 401 from them is a genuine auth failure, not an expired session.
const AUTH_PATHS = ['/auth/refresh', '/auth/login', '/auth/otp', '/auth/social'];
const isAuthPath = (url = '') => {
  const path = String(url).split('?')[0];
  return AUTH_PATHS.some((p) => path.endsWith(p));
};

/**
 * Adds a 401 -> silent-refresh -> retry response interceptor to an existing axios instance.
 * The instance already attaches the bearer access token on each request; this keeps a web
 * session alive (up to the refresh-token's 30-day window) instead of dropping to "Token expired".
 * Single-flight: concurrent 401s share one /auth/refresh call. Anonymous 401s are NOT redirected.
 */
export function installSilentRefresh({ api, accessKey, refreshKey, loginPath }) {
  let refreshing = null;

  const savePair = (data) => {
    if (!data?.access_token || !data?.refresh_token) throw new Error('bad token pair');
    localStorage.setItem(accessKey, data.access_token);
    localStorage.setItem(refreshKey, data.refresh_token);
    return data.access_token;
  };

  const redirectToLogin = () => {
    localStorage.removeItem(accessKey);
    localStorage.removeItem(refreshKey);
    if (window.location.pathname !== loginPath) window.location.assign(loginPath);
  };

  const doRefresh = () => {
    if (!refreshing) {
      const rt = localStorage.getItem(refreshKey);
      if (!rt) return Promise.reject(new Error('no refresh token'));
      // Bare axios so the refresh call itself never re-enters this interceptor.
      refreshing = axios
        .post(`${api.defaults.baseURL}/auth/refresh`, { refresh_token: rt }, { withCredentials: false })
        .then(({ data }) => savePair(data))
        .finally(() => { refreshing = null; });
    }
    return refreshing;
  };

  api.interceptors.response.use(
    (r) => r,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;
      if (!original || status !== 401 || original._retry || isAuthPath(original.url)) {
        return Promise.reject(error);
      }
      if (!localStorage.getItem(refreshKey)) {
        // Anonymous / no session — let the caller's auth provider handle it, don't force a redirect.
        return Promise.reject(error);
      }
      original._retry = true;
      try {
        const newAccess = await doRefresh();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch (e) {
        redirectToLogin();
        return Promise.reject(error);
      }
    },
  );
}
